import type { ReactNode } from 'react';

export interface StudentMetric {
  label: string;
  value: string | number;
  hint?: string;
  tone?: 'cyan' | 'green' | 'purple' | 'neutral';
}

export function StudentMetrics({ items }: { items: StudentMetric[] }) {
  return <div className="student-metrics" aria-label="Learning summary">
    {items.map((item) => <article className={`student-metric student-metric-${item.tone || 'neutral'}`} key={item.label}>
      <span>{item.label}</span><strong>{item.value}</strong>{item.hint && <small>{item.hint}</small>}
    </article>)}
  </div>;
}

export function LessonTimeline({
  blocks,
  completedIds,
  currentId,
  onJump,
}: {
  blocks: Array<{ id: string; title: string }>;
  completedIds: Set<string>;
  currentId?: string;
  onJump: (id: string) => void;
}) {
  return <ol className="lesson-timeline" aria-label="Lesson steps">
    {blocks.map((block, index) => {
      const done = completedIds.has(block.id);
      const current = block.id === currentId;
      return <li className={done ? 'done' : current ? 'current' : ''} key={block.id}>
        <button type="button" onClick={() => onJump(block.id)} aria-current={current ? 'step' : undefined}>
          <span className="lesson-timeline-marker" aria-hidden="true">{done ? '✓' : String(index + 1).padStart(2, '0')}</span>
          <span className="lesson-timeline-copy"><b>{block.title}</b><small>{done ? 'Completed' : current ? 'Next step' : 'Not started'}</small></span>
        </button>
      </li>;
    })}
  </ol>;
}

export function StudentProgressBar({ value, label = 'Progress' }: { value: number; label?: string }) {
  const safeValue = Math.max(0, Math.min(100, Math.round(value)));
  return <div className="student-progress-bar" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={safeValue}>
    <div className="student-progress-bar-top"><span>{label}</span><strong>{safeValue}%</strong></div>
    <div className="student-progress-bar-track"><span style={{ width: `${safeValue}%` }} /></div>
  </div>;
}

export function LessonStepBadge({ status }: { status: 'completed' | 'active' | 'upcoming' }) {
  const label = status === 'completed' ? 'Completed' : status === 'active' ? 'In progress' : 'Next';
  return <span className={`lesson-step-badge lesson-step-badge-${status}`} aria-label={label}>{status === 'completed' ? '✓ ' : status === 'active' ? '● ' : ''}{label}</span>;
}

export function StudentSectionHeader({ eyebrow, title, description, action }: { eyebrow: string; title: string; description?: string; action?: ReactNode }) {
  return <div className="student-section-header"><div><span className="eyebrow">{eyebrow}</span><h2>{title}</h2>{description && <p>{description}</p>}</div>{action}</div>;
}

export function StudentStatGrid({ items, compact = false }: { items: StudentMetric[]; compact?: boolean }) {
  return <div className={compact ? 'student-stat-grid compact' : 'student-stat-grid'}>{items.map((item) => <article className={`student-stat student-stat-${item.tone || 'neutral'}`} key={item.label}><span>{item.label}</span><strong>{item.value}</strong>{item.hint && <small>{item.hint}</small>}</article>)}</div>;
}

export function StudentStatus({ status, label }: { status: 'success' | 'active' | 'locked' | 'neutral'; label: string }) {
  return <span className={`student-status student-status-${status}`}>{status === 'success' ? '✓ ' : status === 'active' ? '● ' : status === 'locked' ? '🔒 ' : ''}{label}</span>;
}

export function StudentCard({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <article className={`student-card ${className}`}>{children}</article>;
}

export function LessonBlockNav({ previous, next, onPrevious, onNext }: { previous?: string; next?: string; onPrevious?: () => void; onNext?: () => void }) {
  if (!previous && !next) return null;
  return <nav className="lesson-block-nav" aria-label="Lesson step navigation">
    <button type="button" className="secondary-action" disabled={!previous} onClick={onPrevious}>← {previous || 'Previous'}</button>
    <button type="button" disabled={!next} onClick={onNext}>{next || 'Next'} →</button>
  </nav>;
}

export function LearningCallout({ eyebrow, title, children, action }: { eyebrow: string; title: string; children: ReactNode; action?: ReactNode }) {
  return <aside className="learning-callout"><div><span className="eyebrow">{eyebrow}</span><h2>{title}</h2><div className="learning-callout-copy">{children}</div></div>{action}</aside>;
}
